import React, { useRef, useState } from "react";
import moment from "moment";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import {
  formatRegisterDobInput,
  REGISTER_DOB_FORMAT,
} from "../Register/Register.validator";

const DOB_ISO_FORMAT = "YYYY-MM-DD";
const DOB_MIN_ISO = "1900-01-01";

const toIsoDate = (value) => {
  if (!value || !moment(value, REGISTER_DOB_FORMAT, true).isValid()) {
    return "";
  }
  return moment(value, REGISTER_DOB_FORMAT).format(DOB_ISO_FORMAT);
};

const RegistrationDobField = ({
  id,
  name,
  value = "",
  onChange,
  onBlur,
  placeholder = REGISTER_DOB_FORMAT,
  className = "",
  disabled = false,
  ...inputProps
}) => {
  const { t } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const nativeDateRef = useRef(null);
  const calendarButtonRef = useRef(null);
  const isoValue = toIsoDate(value);
  const maxIsoDate = moment().format(DOB_ISO_FORMAT);

  const handleInputChange = (event) => {
    const formatted = formatRegisterDobInput(event.target.value);
    onChange?.(formatted, { name, touched: true });
  };

  const handleNativeDateChange = (event) => {
    const nextIso = event.target.value;
    if (!nextIso || !moment(nextIso, DOB_ISO_FORMAT, true).isValid()) {
      return;
    }
    onChange?.(moment(nextIso, DOB_ISO_FORMAT).format(REGISTER_DOB_FORMAT), {
      name,
      touched: true,
    });
    setCalendarOpen(false);
    calendarButtonRef.current?.focus();
  };

  const openNativeDatePicker = () => {
    const input = nativeDateRef.current;
    if (!input) {
      return;
    }
    setCalendarOpen(true);
    try {
      if (typeof input.showPicker === "function") {
        input.showPicker();
        return;
      }
    } catch {
      // Some browsers throw if showPicker cannot open; fall through.
    }
    input.focus();
    input.click();
  };

  return (
    <div className="reg-dob-field">
      <input
        id={id}
        name={name}
        type="text"
        className={clsx("form-input", "reg-dob-input", className)}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onBlur={() => onBlur?.({ target: { name } })}
        disabled={disabled}
        autoComplete="bday"
        inputMode="numeric"
        {...inputProps}
      />
      {!disabled && (
        <>
          <button
            ref={calendarButtonRef}
            type="button"
            className="reg-dob-calendar-btn"
            aria-label={t("NUTRITION_REGISTER_ARIA_OPEN_CALENDAR", {
              defaultValue: "Open calendar",
            })}
            aria-haspopup="dialog"
            aria-expanded={calendarOpen}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              openNativeDatePicker();
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </button>
          <input
            ref={nativeDateRef}
            type="date"
            className="reg-dob-native-picker"
            tabIndex={-1}
            aria-hidden="true"
            value={isoValue}
            min={DOB_MIN_ISO}
            max={maxIsoDate}
            onChange={handleNativeDateChange}
            onBlur={() => {
              setCalendarOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
};

export default RegistrationDobField;
